/** Persistence. The City is plain JSON; save = serialize + defensive load +
 *  base64 export/import. Storage key bumped for the city model. */
import type { City } from './types';
import { createCity, SAVE_VERSION } from './world';
import { artForName } from './config/plots';

export const STORAGE_KEY = 'cloud-nine-city-v3';

export function serialize(c: City): string {
  return JSON.stringify({ ...c, version: SAVE_VERSION });
}

function looksLikeCity(o: unknown): o is City {
  if (!o || typeof o !== 'object') return false;
  const c = o as Record<string, unknown>;
  return Array.isArray(c.buildings) && Array.isArray(c.plots) && typeof c.cash === 'number';
}

export function fromSave(obj: unknown, now: number): City {
  const base = createCity(now);
  if (!looksLikeCity(obj)) return base;
  const c = obj as City;
  return {
    ...base,
    ...c,
    // backfill `art` for buildings + plots saved before bespoke sprites existed
    buildings: c.buildings.map((b) => ({ ...b, art: b.art ?? artForName(b.name) })),
    plots: (c.plots ?? base.plots).map((p) => ({ ...p, art: p.art ?? artForName(p.name) })),
    activeEvents: c.activeEvents ?? [],
    recruit: c.recruit ?? null,
    completedGoals: c.completedGoals ?? [],
    log: c.log ?? base.log,
    toasts: c.toasts ?? [],
    nextToastId: c.nextToastId ?? 1,
    version: SAVE_VERSION,
    lastSeen: now,
    speed: c.speed ?? 1,
  };
}

export function saveToStorage(c: City): void {
  try {
    localStorage.setItem(STORAGE_KEY, serialize(c));
  } catch {
    /* ignore */
  }
}
export function loadFromStorage(now: number): City | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return fromSave(JSON.parse(raw), now);
  } catch {
    return null;
  }
}
export function clearStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function exportSave(c: City): string {
  return base64Encode(serialize(c));
}
export function importSave(b64: string, now: number): City | null {
  try {
    const parsed = JSON.parse(base64Decode(b64.trim()));
    if (!looksLikeCity(parsed)) return null;
    return fromSave(parsed, now);
  } catch {
    return null;
  }
}

function base64Encode(s: string): string {
  const bytes = new TextEncoder().encode(s);
  if (typeof btoa === 'function') {
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin);
  }
  return Buffer.from(bytes).toString('base64');
}
function base64Decode(b64: string): string {
  if (typeof atob === 'function') {
    const bin = atob(b64);
    return new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)));
  }
  return Buffer.from(b64, 'base64').toString('utf-8');
}
